/**
 * Superhero scripts.
 *
 * Handles loading and animating the superhero.
 */

/** Dependencies -------------------------------------------- */

// External

import lottie from 'lottie-web'

import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import ScrollSmoother from 'gsap/ScrollSmoother'

gsap.registerPlugin( ScrollTrigger, ScrollSmoother )

// Design system

import { completionEventName as preloaderCompletionEventName } from '../preloader'

import { colors } from '../blocks/style-guide'

/** The beef ------------------------------------------------ */

/**
 * The completion events to fire off once the superhero is done animating.
 */
export const completionEventName = 'superheroComplete'
export const completionEvent = new Event( completionEventName )

/**
 * Superhero mixin.
 * Provides superhero functionality to single element.
 */
export let mixin = ( { node = null, config = {} } = {} ) => {

    let state = {
        node,
        config,
        backgroundElement     : null,
        animationElement      : null,
        animationInnerElement : null,
        lottieApi             : null,
        playHead              : { frame: 0 },
        timeline              : true,
        isAnimationReady      : false,
        isPreloaderComplete   : false
    };

    let module = {
        
        // Setup

        init() {

            state.backgroundElement = node.querySelector( `.${state.config.className}__background` )
            state.animationElement = node.querySelector( `.${state.config.className}__animation` )
            state.animationInnerElement = node.querySelector( `.${state.config.className}__animation-inner` )

            ScrollSmoother.get().scrollTo( node, false )

            if ( state.animationInnerElement.hasChildNodes() ) {
                state.animationInnerElement.innerHTML = ''
            }

            state.lottieApi = lottie.loadAnimation(
                {
                    path      : state.config.animationPath,
                    container : state.animationInnerElement,
                    renderer  : 'svg',
                    autoplay  : false,
                    loop      : false
                }
            )

            ScrollSmoother.get().effects(
                state.animationElement,
                { speed: state.config.animationParallaxSpeed, lag: state.config.animationParallaxLag }
            )

            ScrollTrigger.create({
                trigger: state.node,
                start  : 'bottom bottom',
            })

            this.setEventBindings()
        },

        setEventBindings() {
            state.lottieApi.addEventListener( 'data_ready', this.onAnimationReady.bind( this ) )
            window.addEventListener( preloaderCompletionEventName, this.onPreloaderComplete.bind( this ) )
        },

        // Handlers

        onAnimationEnd() {

            window.dispatchEvent( completionEvent )

            ScrollTrigger.observe({
                type   : 'touch, wheel, pointer, scroll',
                onDown : ( observer ) => {
                    window.dispatchEvent( scrollIndicatorOutEvent )
                    observer.kill()
                }
            })
        },

        onTimelineUpdate() {
            state.lottieApi.goToAndStop( state.playHead.frame, true )
        },

        onReady() {

            const totalDuration = state.lottieApi.totalFrames / state.lottieApi.frameRate

            const logoGrowStartTime = state.config.logoGrowStartFrame / state.lottieApi.frameRate
            const logoGrowDuration = ( state.config.logoGrowEndFrame - state.config.logoGrowStartFrame ) / state.lottieApi.frameRate
            const backgroundColorChangeStartTime = state.config.backgroundColorChangeStartFrame / state.lottieApi.frameRate
            const backgroundColorChangeDuration = ( state.config.backgroundColorChangeEndFrame - state.config.backgroundColorChangeStartFrame ) / state.lottieApi.frameRate
            const endEventTime = state.config.endEventFrame / state.lottieApi.frameRate

            const logoGrowTimeline = gsap.timeline()
                .to( state.animationInnerElement, { scale: 0.95, duration: logoGrowDuration / 2 } )
                .to( state.animationInnerElement, { scale: 1, duration: logoGrowDuration / 2, ease: 'back.out( 4 )' } )

            state.timeline = gsap.timeline( { onUpdate: this.onTimelineUpdate } )
                .delay( state.config.animationDelay )
                .timeScale( state.config.animationTimeScale )
                .to( state.playHead, { frame: state.lottieApi.totalFrames - 1, ease: 'none', duration : totalDuration } )
                .add( logoGrowTimeline, logoGrowStartTime )
                .to( state.backgroundElement, { backgroundColor: colors.black.hex, duration: backgroundColorChangeDuration }, backgroundColorChangeStartTime )
                .call( this.onAnimationEnd, [], endEventTime )
        },

        onPreloaderComplete() {
            state.isPreloaderComplete = true
            if ( state.isAnimationReady ) {
                this.onReady()
            }
        },

        onAnimationReady() {
            state.isAnimationReady = true
            if ( state.isPreloaderComplete ) {
                this.onReady()
            }
        }

    }

    return module.init()
}

/**
 * Superhero module.
 * Provides an interface to apply superhero functionality to all elements matching a selector.
 */
export default {

    init( { className, config = {} } ) {

        let selected = document.querySelectorAll( `.${className}` )

        if ( ! selected.length || ! config.animationPath ) {
            window.dispatchEvent( completionEvent )
            return
        }

        const defaultConfig = {
            animationDelay                  : 0,
            animationTimeScale              : 1.05,
            logoGrowStartFrame              : 48,
            logoGrowEndFrame                : 70,
            backgroundColorChangeStartFrame : 56,
            backgroundColorChangeEndFrame   : 120,
            endEventFrame                   : 185,
            animationParallaxSpeed          : 0.75,
            animationParallaxLag            : 0.2
        }

        return Array.from( selected ).map(
			( node, index ) => mixin( { node, config: { ...defaultConfig, ...config, className } } )
        )
    }
}
